import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { PageTransition } from "@/components/PageTransition";

import viktorasPhoto from "@/assets/team-viktoras.jpg";
import daliusPhoto from "@/assets/team-dalius.jpg";
import agotaPhoto from "@/assets/team-agota.jpg";

const teamMembers = [
  {
    name: "Viktoras Viršilas",
    title: "The Founder",
    since: "Since 1974",
    description: "A master of real estate cultural heritage and construction technologies, Viktoras established the foundation upon which three generations now build. His deep understanding of architectural preservation and building excellence has guided our family's approach to property for over five decades.",
    expertise: ["Cultural Heritage", "Construction Technologies", "Architectural Preservation"],
    photo: viktorasPhoto,
  },
  {
    name: "Dalius Viršilas",
    title: "CEO & Co-Founder",
    since: "30+ Years Experience",
    description: "With three decades navigating international markets from Hong Kong to Barcelona, Turin to Dubai, Dalius brings unparalleled legal expertise and operational excellence to every transaction. His strategic vision has expanded our reach across continents while maintaining the discretion our clients expect.",
    expertise: ["International Law", "Cross-Border Development", "Strategic Operations"],
    photo: daliusPhoto,
  },
  {
    name: "Agota Viršilaitė",
    title: "Investment Specialist",
    since: "Next Generation",
    description: "Representing the future of our legacy, Agota combines inherited expertise with fresh perspective on luxury markets. Her intuitive understanding of discerning clients and meticulous approach to property selection ensures every acquisition aligns with generational wealth objectives.",
    expertise: ["Luxury Acquisition", "Client Relations", "Market Intelligence"],
    photo: agotaPhoto,
  },
];

const principles = [
  {
    title: "Discretion",
    description: "Privacy is paramount. Every transaction conducted with absolute confidentiality.",
  },
  {
    title: "Excellence",
    description: "Uncompromising standards in every property we represent and service we provide.",
  },
  {
    title: "Legacy",
    description: "Building generational wealth through properties that transcend market cycles.",
  },
  {
    title: "Integrity",
    description: "Our reputation, built over decades, is protected in every interaction.",
  },
];

const Team = () => {
  return (
    <PageTransition>
      <main className="min-h-screen bg-background">
        <Navbar />

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 bg-gradient-to-b from-cream to-cream-dark overflow-hidden">
          {/* Subtle texture overlay */}
          <div 
            className="absolute inset-0 opacity-[0.02]" 
            style={{ 
              backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\'/%3E%3C/svg%3E")' 
            }} 
          />
          
          {/* Decorative orbs */}
          <div className="absolute top-20 left-10 w-64 h-64 bg-gold/5 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-gold/3 rounded-full blur-3xl pointer-events-none" />

          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              className="max-w-4xl mx-auto text-center"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.p
                className="text-gold font-display text-sm tracking-[0.3em] uppercase mb-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                The Viršilas Legacy
              </motion.p>

              <motion.h1
                className="font-display text-4xl md:text-5xl lg:text-6xl text-charcoal mb-8 leading-tight"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.8 }}
              >
                Three Generations of
                <span className="block text-gold-dark italic mt-2">Quiet Excellence</span>
              </motion.h1>

              <motion.p
                className="text-charcoal-light text-lg md:text-xl max-w-2xl mx-auto leading-relaxed font-body"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.8 }}
              >
                Since 1974, our family has cultivated relationships and expertise that
                transcend ordinary real estate practice. What began as one man's vision
                has become a dynasty of trusted advisors to the world's most discerning investors.
              </motion.p>

              {/* Heritage Badge */}
              <motion.div
                className="mt-12 inline-flex items-center gap-8 px-8 py-4 border border-gold/20 rounded-sm bg-white/50 backdrop-blur-sm shadow-subtle"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
              >
                <div className="text-center">
                  <p className="font-display text-3xl text-gold-dark">50+</p>
                  <p className="text-xs text-charcoal-light tracking-wider uppercase">Years</p>
                </div>
                <div className="w-px h-10 bg-gold/20" />
                <div className="text-center">
                  <p className="font-display text-3xl text-gold-dark">3</p>
                  <p className="text-xs text-charcoal-light tracking-wider uppercase">Generations</p>
                </div>
                <div className="w-px h-10 bg-gold/20" />
                <div className="text-center">
                  <p className="font-display text-3xl text-gold-dark">4</p>
                  <p className="text-xs text-charcoal-light tracking-wider uppercase">Markets</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Team Members - Editorial Layout */}
        <section className="py-20 md:py-32 bg-white">
          <div className="container mx-auto px-4">
            <div className="space-y-24 md:space-y-32">
              {teamMembers.map((member, index) => (
                <motion.article
                  key={member.name}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center ${
                    index % 2 === 1 ? "lg:grid-flow-dense" : ""
                  }`}
                  initial={{ opacity: 0, y: 60 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8 }}
                >
                  {/* Photo */}
                  <div className={`relative ${index % 2 === 1 ? "lg:col-start-2" : ""}`}>
                    <div className="relative overflow-hidden aspect-[3/4] md:aspect-[4/5]">
                      {/* Subtle frame effect */}
                      <div className="absolute inset-0 border border-gold/10 z-10 pointer-events-none" />

                      <motion.img
                        src={member.photo}
                        alt={member.name}
                        className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700"
                        whileHover={{ scale: 1.02 }}
                        transition={{ duration: 0.6 }}
                      />

                      {/* Subtle vignette overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-charcoal/10 via-transparent to-transparent pointer-events-none" />
                    </div>

                    {/* Decorative corner accent */}
                    <div className="absolute -bottom-4 -right-4 w-24 h-24 border-r border-b border-gold/20 pointer-events-none hidden lg:block" />
                  </div>

                  {/* Content */}
                  <div className={`${index % 2 === 1 ? "lg:col-start-1 lg:row-start-1" : ""} space-y-6`}>
                    <div>
                      <p className="text-gold text-sm tracking-[0.2em] uppercase mb-2">{member.since}</p>
                      <h2 className="font-display text-3xl md:text-4xl text-charcoal mb-2">{member.name}</h2>
                      <p className="text-gold-dark font-display text-xl italic">{member.title}</p>
                    </div>

                    <div className="w-16 h-px bg-gold/30" />

                    <p className="text-charcoal-light text-lg leading-relaxed font-body">
                      {member.description}
                    </p>

                    <div className="pt-4">
                      <p className="text-xs text-charcoal/50 uppercase tracking-widest mb-3">Areas of Expertise</p>
                      <div className="flex flex-wrap gap-3">
                        {member.expertise.map((skill) => (
                          <span
                            key={skill}
                            className="px-4 py-2 text-sm text-charcoal/70 border border-charcoal/10 bg-cream/50 rounded-sm"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        {/* Principles Section */}
        <section className="py-20 md:py-28 bg-gradient-to-b from-cream-dark/50 to-cream">
          <div className="container mx-auto px-4">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-gold font-display text-sm tracking-[0.3em] uppercase mb-4">Our Foundation</p>
              <h2 className="font-display text-3xl md:text-4xl text-charcoal">Principles That Endure</h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
              {principles.map((principle, index) => (
                <motion.div
                  key={principle.title}
                  className="text-center p-8"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <h3 className="font-display text-xl text-charcoal mb-4">{principle.title}</h3>
                  <p className="text-charcoal-light text-sm leading-relaxed font-body">
                    {principle.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Legacy Statement */}
        <section className="py-20 md:py-28 bg-charcoal relative overflow-hidden">
          {/* Subtle pattern */}
          <div 
            className="absolute inset-0 opacity-5" 
            style={{ 
              backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23D4AF37\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' 
            }} 
          />

          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              className="max-w-4xl mx-auto text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-gold/80 font-display text-sm tracking-[0.3em] uppercase mb-8">Our Legacy</p>

              <blockquote className="font-display text-2xl md:text-3xl lg:text-4xl text-cream/90 leading-relaxed mb-8 italic">
                "We do not merely facilitate transactions. We steward the transfer of
                extraordinary properties between those who understand their true worth."
              </blockquote>

              <div className="flex items-center justify-center gap-4">
                <div className="w-12 h-px bg-gold/40" />
                <p className="text-gold text-sm tracking-wider">The Viršilas Family</p>
                <div className="w-12 h-px bg-gold/40" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 md:py-28 bg-cream">
          <div className="container mx-auto px-4">
            <motion.div
              className="max-w-3xl mx-auto text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-display text-3xl md:text-4xl text-charcoal mb-6">Begin a Conversation</h2>
              <p className="text-charcoal-light text-lg mb-10 max-w-xl mx-auto font-body">
                Whether you seek an exceptional property or wish to discreetly list one,
                we welcome the opportunity to understand your objectives.
              </p>

              <Button variant="luxury" size="xl" asChild>
                <Link to="/#contact">
                  Schedule a Private Consultation
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </motion.div>
          </div>
        </section>

        <Footer />
      </main>
    </PageTransition>
  );
};

export default Team;
